import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Download, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

// Mock data for payments
const mockPayments = [
  {
    id: 1,
    ref: 'PAY-2024-001',
    bankAccount: 'FR76 1234 5678 9012 3456 7890 12',
    paymentMethod: 'Virement bancaire',
    amount: 2450.80,
    date: '2024-01-15',
    status: 'paid'
  },
  {
    id: 2,
    ref: 'PAY-2024-002',
    bankAccount: 'FR76 1234 5678 9012 3456 7890 12',
    paymentMethod: 'Carte bancaire',
    amount: 1320.50,
    date: '2024-01-10',
    status: 'processing'
  },
  {
    id: 3,
    ref: 'PAY-2024-003',
    bankAccount: 'FR76 1234 5678 9012 3456 7890 12',
    paymentMethod: 'Chèque',
    amount: 890.00,
    date: '2024-01-08',
    status: 'unpaid'
  },
  {
    id: 4,
    ref: 'PAY-2024-004',
    bankAccount: 'FR76 1234 5678 9012 3456 7890 12',
    paymentMethod: 'Virement bancaire',
    amount: 3200.75,
    date: '2024-01-05',
    status: 'paid'
  },
  {
    id: 5,
    ref: 'PAY-2024-005',
    bankAccount: 'FR76 1234 5678 9012 3456 7890 12',
    paymentMethod: 'Carte bancaire',
    amount: 1580.30,
    date: '2024-01-03',
    status: 'processing'
  },
  {
    id: 6,
    ref: 'PAY-2024-006',
    bankAccount: 'FR76 1234 5678 9012 3456 7890 12',
    paymentMethod: 'Prélèvement',
    amount: 750.00,
    date: '2024-01-01',
    status: 'unpaid'
  }
];

const ITEMS_PER_PAGE = 5;

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'paid':
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Payé</Badge>;
    case 'processing':
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">En cours de traitement</Badge>;
    case 'unpaid':
      return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Impayé</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export const PaymentsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter payments based on search term and filters
  const filteredPayments = mockPayments.filter(payment => {
    const matchesSearch = 
      payment.ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.bankAccount.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.paymentMethod.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    const matchesPaymentMethod = paymentMethodFilter === 'all' || payment.paymentMethod === paymentMethodFilter;
    
    return matchesSearch && matchesStatus && matchesPaymentMethod;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPayments.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedPayments = filteredPayments.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleExport = () => {
    // Mock export functionality
    console.log('Exporting payments...');
    // In real implementation, this would generate and download a CSV/Excel file
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: 'MAD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Mes Paiements
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par référence, compte ou mode de paiement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="paid">Payé</SelectItem>
              <SelectItem value="processing">En cours de traitement</SelectItem>
              <SelectItem value="unpaid">Impayé</SelectItem>
            </SelectContent>
          </Select>
          <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Mode de paiement" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les modes</SelectItem>
              <SelectItem value="Virement bancaire">Virement bancaire</SelectItem>
              <SelectItem value="Carte bancaire">Carte bancaire</SelectItem>
              <SelectItem value="Chèque">Chèque</SelectItem>
              <SelectItem value="Prélèvement">Prélèvement</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Payments Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Référence</TableHead>
                <TableHead>Compte Bancaire</TableHead>
                <TableHead>Mode de paiement</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPayments.length > 0 ? (
                paginatedPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell className="font-medium">{payment.ref}</TableCell>
                    <TableCell className="font-mono text-sm">{payment.bankAccount}</TableCell>
                    <TableCell>{payment.paymentMethod}</TableCell>
                    <TableCell className="font-semibold">{formatAmount(payment.amount)}</TableCell>
                    <TableCell>{formatDate(payment.date)}</TableCell>
                    <TableCell>{getStatusBadge(payment.status)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Aucun paiement trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Affichage de {startIndex + 1} à {Math.min(startIndex + ITEMS_PER_PAGE, filteredPayments.length)} sur {filteredPayments.length} paiements
            </p>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Précédent
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className="w-8 h-8"
                  >
                    {page}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Suivant
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};